import React, { useEffect, useState } from "react";
import axios from "axios";
import { Clock } from "lucide-react";
import { getTrialExpiresAt } from "../../utils/organizationService";
import PlanPickerModal from "./PlanPickerModal";

function readOrganization() {
  try {
    return JSON.parse(localStorage.getItem("organization"));
  } catch {
    return null;
  }
}

/** Cheap local check so paid orgs never call check-subscription from the sidebar. */
function mayBeOnDatabaseTrial(org) {
  if (org?.subscription_details?.M?.sub_status?.S !== "trial") return false;
  const expiresAt = getTrialExpiresAt(org);
  return expiresAt != null && expiresAt > Math.floor(Date.now() / 1000);
}

/**
 * Days left in a database trial, or null when not on one. check-subscription
 * decides: it answers trialSource "database" only when Stripe has no live or
 * owing subscription, so a customer who already checked out (and is trialing
 * in Stripe until the trial end) does not see the note.
 */
function useDatabaseTrialDaysLeft() {
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    const org = readOrganization();
    if (!mayBeOnDatabaseTrial(org)) return undefined;
    let cancelled = false;
    axios
      .post(process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription", {
        organization_id: org.orgId?.S,
        customer_id: org.subscription_details?.M?.stripe_cust_id?.S,
      })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.trialSource !== "database" || !data?.isActive) return;
        const secondsLeft = Number(data.subscriptionEndDate) - Math.floor(Date.now() / 1000);
        if (secondsLeft > 0) setDaysLeft(Math.ceil(secondsLeft / 86400));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return daysLeft;
}

/**
 * Trial countdown for the bottom of the sidebar. The plan link is shown only
 * to members who can manage billing.
 */
export default function TrialSidebarNote({ canChoosePlan = true, className = "" }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const daysLeft = useDatabaseTrialDaysLeft();
  if (daysLeft == null) return null;

  return (
    <div className={className}>
      <div className="rounded-lg bg-[rgba(96,91,255,0.1)] px-3 py-2.5 text-[13px]">
        <div className="flex items-center gap-1.5 font-semibold text-[#111827]">
          <Clock className="w-4 h-4 shrink-0 text-[#605BFF]" />
          {daysLeft === 1 ? "1 day left in your trial" : `${daysLeft} days left in your trial`}
        </div>
        {canChoosePlan && (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="mt-1 font-semibold text-[#605BFF] hover:underline"
          >
            Select a plan
          </button>
        )}
      </div>
      {canChoosePlan && (
        <PlanPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} />
      )}
    </div>
  );
}
